import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import axios from "axios";
import { CursorProvider } from "./components/CustomCursor";
import Layout from "./components/Layout/Layout";
import ScrollToTop from "./components/ScrollToTop";
import ChatbotWidget from "./components/Chatbot/ChatbotWidget";

// Lazy-loaded page components
const Home = lazy(() => import("./pages/Home"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Conferences = lazy(() => import("./pages/Conferences"));
const Hybrids = lazy(() => import("./pages/Hybrids"));
const Webinars = lazy(() => import("./pages/Webinars"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const Blog = lazy(() => import("./pages/Blog"));
const Journals = lazy(() => import("./pages/Journals"));
const Contact = lazy(() => import("./pages/Contact"));
const BuyATicket = lazy(() => import("./pages/BuyATicket"));
const Committees = lazy(() => import("./pages/Committees"));
const Speakers = lazy(() => import("./pages/Speakers"));
const Schedule = lazy(() => import("./pages/Schedule"));
const Gallery = lazy(() => import("./components/Gallery/QuantumGallery"));
const CallForPapers = lazy(() => import("./pages/CallForPapers"));
const SuccessPage = lazy(() => import("./pages/SuccessPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const RegistrationForm = lazy(() => import("./pages/RegistrationPageUpdated"));
const AllConferences = lazy(() => import("./pages/AllConferences"));

// Simple loading spinner
const LoadingFallback = () => (
  <div className="flex justify-center items-center h-screen bg-slate-50">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-600 border-solid"></div>
  </div>
);

// Define your API Base URL
// It's highly recommended to use environment variables for this in a real project:
// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
// For demonstration, hardcoding for now, but switch to env var in production.
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "https://backend-code-6vqy.onrender.com";

function App() {
  useEffect(() => {
    const keepServerAwake = () => {
      fetch(`${API_BASE_URL}/`)
        .then(() => console.log("✅ Warm-up ping sent to backend."))
        .catch((err) => console.warn("⚠️ Warm-up ping failed:", err.message));
    };

    // Ping immediately on load
    keepServerAwake();

    // ⏱️ Ping every 1 minute (60,000 ms)
    const interval = setInterval(keepServerAwake, 60000);

    // Clear interval on unmount
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const recordVisit = async () => {
      try {
        await axios.post(`${API_BASE_URL}/api/record-visit`, {}, { withCredentials: true });
        console.log("✅ Visit recorded successfully.");
      } catch (err) {
        console.warn("⚠️ Could not record visit:", err.message);
      }
    };
    
    recordVisit();
  }, []);

  useEffect(() => {
    const fetchAndStoreSourceToken = async () => {
      if (!localStorage.getItem("sourceToken")) {
        try {
          const urlParams = new URLSearchParams(window.location.search);
          const sourceId = urlParams.get("sourceId");
          const conferenceType = urlParams.get("conferenceType");
          const conferenceYear = urlParams.get("conferenceYear"); // if backend requires it

          if (!sourceId || !conferenceType) {
            console.error("❌ Missing sourceId or conferenceType in URL");
            return;
          }

          console.log(
            `Attempting to fetch source token for sourceId: ${sourceId}, conferenceType: ${conferenceType}, conferenceYear: ${
              conferenceYear || "N/A"
            }`
          );

          const response = await axios.get(
            `${API_BASE_URL}/api/source/generate-token?sourceId=${sourceId}&conferenceType=${conferenceType}${
              conferenceYear ? `&conferenceYear=${conferenceYear}` : ""
            }`
          );

          const token = response.data.token;
          localStorage.setItem("sourceToken", token);
          console.log("✅ Successfully fetched and stored new source token.");
        } catch (error) {
          console.error(
            "❌ Error fetching source token:",
            error.response ? error.response.data : error.message
          );
        }
      } else {
        console.log("Source token already exists in localStorage.");
      }
    };

    fetchAndStoreSourceToken();
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <CursorProvider>
        <Layout>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<AboutUs />} />
              {/* <Route path="/conferences" element={<Conferences />} /> */}
              <Route path="/conference" element={<Hybrids />} />
              <Route path="/webinars" element={<Webinars />} />
              <Route path="/testimonials" element={<Testimonials />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/journals" element={<Journals />} />
              <Route path="/contact" element={<Contact />} />
              {/* Call for papers */}
              <Route path="/call-for-papers" element={<CallForPapers />}></Route>
              <Route path="/buy-a-ticket" element={<BuyATicket />} />
              <Route path="/committees" element={<Committees />} />
              <Route path="/speakers" element={<Speakers />} />
              <Route path="/schedule" element={<Schedule />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/call-for-papers" element={<CallForPapers />} />
              <Route
                path="/Conferences"
                element={<AllConferences />}
              ></Route>
              {/* --- Routes for the Registration Form --- */}
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        {/* <Route path="/registration/:slug" element={<RegistrationForm/>} /> */}

              <Route path="/success" element={<SuccessPage />} />

  <Route path="/registration/:slug" element={<RegistrationForm />} />

              {/* Catch-all route for 404 Not Found pages */}
              <Route path="*" element={<NotFound />} />

            </Routes>
          </Suspense>
        </Layout>
      </CursorProvider>
      <ChatbotWidget />
    </Router>
  );
}

export default App;
